import _ from 'lodash';

import { FeeProviderLocationType } from 'constants/feeProviders';
import { AttendantCompanyTypeEnum } from 'constants/loanApplication';
import { getCleanPhoneNumber, normalizeRequestPhoneNumber } from 'utils/data';
import { getCommonDateBody } from 'utils/date';

import {
  getBrokerFieldIds,
  getDealerFieldIds,
  getInvestorFieldIds,
  MH_BROKER_PREFIX,
  MH_DEALER_PREFIX,
  MH_INVESTOR_PREFIX,
} from '../EditDrawer.manufacturedHomeContactsConfig';

import {
  getClosingAttendantCompanies,
  getFieldWithFallback,
  isCompanySectionChanged,
} from './Content.utils';

export const MANUFACTURED_HOME_CONTACT_CONFIGS = [
  {
    prefix: MH_BROKER_PREFIX,
    categoryType: AttendantCompanyTypeEnum.BROKER,
    closingCompanyType: AttendantCompanyTypeEnum.BROKER,
    getFieldIds: getBrokerFieldIds,
  },
  {
    prefix: MH_DEALER_PREFIX,
    categoryType: AttendantCompanyTypeEnum.DEALER,
    closingCompanyType: AttendantCompanyTypeEnum.DEALER,
    getFieldIds: getDealerFieldIds,
  },
  {
    prefix: MH_INVESTOR_PREFIX,
    categoryType: AttendantCompanyTypeEnum.INVESTOR,
    closingCompanyType: AttendantCompanyTypeEnum.INVESTOR,
    getFieldIds: getInvestorFieldIds,
  },
];

// The Broker/Dealer/Investor Fee Provider categories aren't exposed by the
// `/attendant-companies` category-id map (that endpoint only resolves Title/Escrow/
// Attorney), so we resolve them the same way the backend does for those three:
// case-insensitive match against the category name.
export const getFeeProviderCategoryIdByName = (feeProviderCategories, name) =>
  feeProviderCategories?.find(category => _.toLower(category.name) === _.toLower(name))
    ?.feeProviderCategoryId;

export const getManufacturedHomeContactCategoryIds = feeProviderCategories =>
  MANUFACTURED_HOME_CONTACT_CONFIGS.reduce((acc, { categoryType }) => {
    acc[categoryType] = getFeeProviderCategoryIdByName(feeProviderCategories, categoryType);

    return acc;
  }, {});

export const getFeeProviderOptionsByCategoryId = (feeProviderOptions, categoryId) =>
  _.sortBy(
    feeProviderOptions?.filter(
      option => !!categoryId && option.feeProviderCategoryId === categoryId,
    ),
    'name',
  );

export const getPrimaryFeeProviderLocation = option =>
  option?.locations?.find(({ type }) => type === FeeProviderLocationType.PRIMARY) ??
  option?.locations?.[0] ??
  {};

const buildContactApplicationFields = (company, prefix, getFieldIds, extraKeys) => {
  if (!company) {
    return {};
  }

  const fieldId = getFieldIds(prefix);

  return {
    [fieldId.attendantCompanyId]: company.attendantCompanyId || '',
    [fieldId.attendantCompanyLocationId]:
      company.closingCompanyLocationId || company.feeProviderLocationId || '',
    [fieldId.name]: company.name || '',
    [fieldId.streetAddress]: company.streetAddress || '',
    [fieldId.city]: company.city || '',
    [fieldId.state]: company.state || '',
    [fieldId.zipCode]: company.zipCode || '',
    [fieldId.phone]: getCleanPhoneNumber(company.phone) || '',
    [fieldId.fax]: company.fax || '',
    [fieldId.contactPerson]: company.contactPerson || '',
    [fieldId.contactEmail]: company.contactEmail || '',
    ..._.reduce(
      extraKeys,
      (acc, key) => {
        acc[fieldId[key]] = company[key] || '';

        return acc;
      },
      {},
    ),
  };
};

export const buildBrokerApplicationFields = company =>
  buildContactApplicationFields(company, MH_BROKER_PREFIX, getBrokerFieldIds, [
    'licenseNumber',
    'accountManager',
  ]);

// Contract Date is sourced from ChattelDetails (a separate entity from the Dealer
// attendant company), so it is seeded independently rather than through `extraKeys`
// (which reads off the company object).
export const buildDealerApplicationFields = (company, contractDate) => ({
  ...buildContactApplicationFields(company, MH_DEALER_PREFIX, getDealerFieldIds, [
    'contactCategory',
    'accountManager',
    'salespersonName',
    'salespersonPhone',
    'salespersonEmail',
  ]),
  [getDealerFieldIds(MH_DEALER_PREFIX).contractDate]: contractDate,
});

export const buildInvestorApplicationFields = company =>
  buildContactApplicationFields(company, MH_INVESTOR_PREFIX, getInvestorFieldIds, ['channel']);

// Populates the read-only/editable detail fields once a Broker/Dealer/Investor is
// selected from the lookup, sourced from the Fee Provider's primary location plus
// the provider-level catalog fields (fax/accountManager/channel/salesperson) added
// by the Skynet.Pricing FeeProvider catalog work.
export const buildManufacturedHomeContactAutofill = ({ option, prefix, getFieldIds }) => {
  const fieldId = getFieldIds(prefix);
  const location = getPrimaryFeeProviderLocation(option);

  return {
    [fieldId.attendantCompanyId]: option,
    [fieldId.attendantCompanyLocationId]: location.feeProviderLocationId || '',
    [fieldId.name]: option.name || '',
    [fieldId.streetAddress]: location.streetAddress || '',
    [fieldId.city]: location.city || '',
    [fieldId.state]: location.state || '',
    [fieldId.zipCode]: location.zipCode || '',
    [fieldId.phone]: getCleanPhoneNumber(location.phone) || '',
    [fieldId.fax]: option.fax || '',
    [fieldId.contactPerson]: location.contactPerson || '',
    [fieldId.contactEmail]: location.contactEmail || '',
    ...(fieldId.licenseNumber && { [fieldId.licenseNumber]: location.licenseNumber || '' }),
    ...(fieldId.accountManager && { [fieldId.accountManager]: option.accountManager || '' }),
    ...(fieldId.channel && { [fieldId.channel]: option.channel || '' }),
    // Dealer's "Contact Category" has no backing field on the FeeProvider model —
    // this section is inherently scoped to the Dealer category, so the read-only
    // display always reflects that.
    ...(fieldId.contactCategory && { [fieldId.contactCategory]: AttendantCompanyTypeEnum.DEALER }),
    // Salesperson Phone/Email have no backing field on FeeProviderDto (only a single
    // `salesperson` string exists) — rendered per the design but not persisted today.
    ...(fieldId.salespersonName && { [fieldId.salespersonName]: option.salesperson || '' }),
    ...(fieldId.salespersonPhone && { [fieldId.salespersonPhone]: '' }),
    ...(fieldId.salespersonEmail && { [fieldId.salespersonEmail]: '' }),
  };
};

// Builds a ClosingAttendantCompanyRequestDto-shaped entry. The current backend
// location DTO (LoanClosingAttendantCompanyLocationRequestDto) has no Fax/
// AccountManager/Channel/Salesperson/ContactCategory fields, so those never
// round-trip through this payload — only what Title/Escrow/Attorney already send.
export const buildManufacturedHomeContactPayloadEntry = ({
  formData,
  company,
  prefix,
  getFieldIds,
  closingCompanyType,
}) => {
  const fieldId = getFieldIds(prefix);
  const rawId = formData[fieldId.attendantCompanyId];
  const attendantCompanyId = (rawId?.value ?? rawId) || null;

  if (!attendantCompanyId) {
    return null;
  }

  const locationId = getFieldWithFallback(
    formData,
    fieldId.attendantCompanyLocationId,
    company?.closingCompanyLocationId || company?.feeProviderLocationId || null,
  );

  return {
    attendantCompanyId,
    closingCompanyType,
    location: {
      attendantCompanyLocationId: locationId,
      name: formData[fieldId.name] || company?.name || '',
      streetAddress: formData[fieldId.streetAddress] || company?.streetAddress || '',
      city: formData[fieldId.city] || company?.city || '',
      state: formData[fieldId.state] || company?.state || '',
      zipCode: formData[fieldId.zipCode] || company?.zipCode || '',
      phone: normalizeRequestPhoneNumber(formData[fieldId.phone]) || company?.phone || '',
      email: formData[fieldId.contactEmail] || company?.contactEmail || '',
      contactPerson: formData[fieldId.contactPerson] || company?.contactPerson || '',
      contactPhone: '',
      contactEmail: formData[fieldId.contactEmail] || company?.contactEmail || '',
    },
  };
};

export const getManufacturedHomeContactsAttendantCompanies = ({
  formData,
  brokerCompany,
  dealerCompany,
  investorCompany,
}) => {
  const companiesByType = {
    [AttendantCompanyTypeEnum.BROKER]: brokerCompany,
    [AttendantCompanyTypeEnum.DEALER]: dealerCompany,
    [AttendantCompanyTypeEnum.INVESTOR]: investorCompany,
  };

  return MANUFACTURED_HOME_CONTACT_CONFIGS.map(({ prefix, getFieldIds, closingCompanyType }) =>
    buildManufacturedHomeContactPayloadEntry({
      formData,
      company: companiesByType[closingCompanyType],
      prefix,
      getFieldIds,
      closingCompanyType,
    }),
  ).filter(Boolean);
};

export const isManufacturedHomeContactsSectionChanged = ({
  formData,
  initialFormData,
  brokerCompany,
  dealerCompany,
  investorCompany,
}) => {
  const current = getManufacturedHomeContactsAttendantCompanies({
    formData,
    brokerCompany,
    dealerCompany,
    investorCompany,
  });
  const initial = getManufacturedHomeContactsAttendantCompanies({
    formData: initialFormData,
    brokerCompany,
    dealerCompany,
    investorCompany,
  });

  return !_.isEqual(current, initial);
};

// The closing-company save endpoint replaces the FULL list on every PUT, so Broker/
// Dealer/Investor must always be sent alongside Title/Escrow/Attorney — leaving an
// untouched type out of the array deletes it server-side.
export const getAllClosingAttendantCompanies = ({
  formData,
  titleCompany,
  escrowCompany,
  sellerAttorney,
  buyerAttorney,
  brokerCompany,
  dealerCompany,
  investorCompany,
}) => [
  ...getClosingAttendantCompanies({
    formData,
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
  }),
  ...getManufacturedHomeContactsAttendantCompanies({
    formData,
    brokerCompany,
    dealerCompany,
    investorCompany,
  }),
];

export const getIsAnyClosingCompanySectionChanged = ({
  formData,
  initialFormData,
  titleCompany,
  escrowCompany,
  sellerAttorney,
  buyerAttorney,
  brokerCompany,
  dealerCompany,
  investorCompany,
}) =>
  isCompanySectionChanged({
    formData,
    initialFormData,
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
  }) ||
  isManufacturedHomeContactsSectionChanged({
    formData,
    initialFormData,
    brokerCompany,
    dealerCompany,
    investorCompany,
  });

// ChattelDetails.ContractDate lives on a different entity than the closing attendant
// companies (chattel-details, not LoanClosingAttendantCompany), so it is saved via a
// separate request — this builds the comparable shape used to detect whether it changed.
export const getChattelContractDatePayload = formData => ({
  contractDate: getCommonDateBody(formData[getDealerFieldIds(MH_DEALER_PREFIX).contractDate]),
});

// UpdateChattelDetailsCommandHandler overwrites every field on the entity from the
// request body, so the save must spread the full, previously-fetched ChattelDetails DTO
// and overlay only Contract Date — never send a partial DTO.
export const buildChattelDetailsSavePayload = ({ chattelDetails, contractDate }) => ({
  ...chattelDetails,
  contractDate,
});
